import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { api } from "../actions/api";
import { formatDate } from "../helpers/DateHelper";

const CalendarComponent = forwardRef(({ onDateSelect, initialDate }, ref) => {
  const [currentDate, setCurrentDate] = useState(initialDate || new Date());
  const [days, setDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(initialDate || new Date());
  const [weekTaskStatus, setWeekTaskStatus] = useState({});

  const generateDays = (date) => {
    const daysArray = [];

    const startOfWeek = new Date(date);
    const dayOfWeek = startOfWeek.getDay();

    const diff = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek;
    startOfWeek.setDate(startOfWeek.getDate() + diff);

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      daysArray.push(day);
    }

    setDays(daysArray);

    const weekStart = new Date(startOfWeek);
    const weekEnd = new Date(startOfWeek);
    weekEnd.setDate(startOfWeek.getDate() + 6);

    fetchWeekTaskStatus(weekStart, weekEnd);
  };

  const fetchWeekTaskStatus = async (startDate, endDate) => {
    try {
      const fromDate = formatDate(startDate);
      const toDate = formatDate(endDate);

      const response = await api.get(
        `tasks/mit-weekly/status?from=${fromDate}&to=${toDate}`,
      );

      if (
        response.data &&
        response.data.data &&
        response.data.data.taskStatus
      ) {
        const statusByDate = {};
        response.data.data.taskStatus.forEach((dayStatus) => {
          statusByDate[dayStatus.date] = dayStatus;
        });
        setWeekTaskStatus(statusByDate);
      }
    } catch (error) {
      console.error("Error fetching week task status:", error);
      setWeekTaskStatus({});
    }
  };

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refreshWeekStatus: () => {
      // Refresh the current week's status
      generateDays(currentDate);
    },
  }));

  // Handle "next week" button click
  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
    generateDays(newDate);

    // Auto-select appropriate date for the new week
    autoSelectDateForWeek(newDate);
  };

  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
    generateDays(newDate);

    // Auto-select appropriate date for the new week
    autoSelectDateForWeek(newDate);
  };

  // Auto-select date when week changes
  const autoSelectDateForWeek = (weekDate) => {
    const today = new Date();
    const startOfWeek = new Date(weekDate);
    const dayOfWeek = startOfWeek.getDay();
    const diff = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek;
    startOfWeek.setDate(startOfWeek.getDate() + diff);

    // Check if today is in this week
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    let dateToSelect;
    if (today >= startOfWeek && today <= endOfWeek) {
      // If today is in this week, select today
      dateToSelect = new Date(today);
    } else {
      // Otherwise, select the start of the week (Monday)
      dateToSelect = new Date(startOfWeek);
    }

    setSelectedDate(dateToSelect);
    if (onDateSelect) {
      onDateSelect(dateToSelect);
    }
  };

  const isCurrentDay = (day) => {
    const today = new Date();
    return (
      day.getDate() === today.getDate() &&
      day.getMonth() === today.getMonth() &&
      day.getFullYear() === today.getFullYear()
    );
  };

  const getDayTaskStatus = (day) => {
    const dateKey = formatDate(day);
    return weekTaskStatus[dateKey];
  };

  const handleDayClick = (day) => {
    setSelectedDate(day);
    if (onDateSelect) {
      onDateSelect(day);
    }
  };

  const isPastDay = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    const dayToCheck = new Date(day);
    dayToCheck.setHours(0, 0, 0, 0); // Reset time to start of day
    return dayToCheck < today;
  };

  useEffect(() => {
    generateDays(currentDate);
    // Initialize with appropriate date for the current week
    // Only auto-select if we don't have an initialDate or if it's the first render
  }, [currentDate]);

  // Initial setup
  useEffect(() => {
    if (initialDate) {
      setCurrentDate(initialDate);
      setSelectedDate(initialDate);
    } else {
      autoSelectDateForWeek(new Date());
    }
  }, []);

  return (
    <div className="calender-container">
      <div className="calender-btn" id="prevWeek" onClick={handlePrevWeek}>
        <img
          src="/icons/calender-prev-day.svg"
          alt="Previous Week"
          style={{ cursor: "pointer" }}
        />
      </div>
      <div
        className="calender-days"
        id="daysContainer"
        style={{ cursor: "pointer" }}
      >
        {days.map((day, index) => {
          const dayStatus = getDayTaskStatus(day);
          const isSelected = selectedDate.toDateString() === day.toDateString();
          const isCurrent = isCurrentDay(day);
          const isPast = isPastDay(day);

          return (
            <div
              className={`calender-day-item ${isSelected ? "selected" : ""}`}
              key={index}
              onClick={() => handleDayClick(day)}
            >
              <div className="d-flex flex-column align-items-center">
                <span
                  className="text-body-b1"
                  style={{
                    color: isSelected ? "#008cff" : "#d0d8df",
                  }}
                >
                  {day.toLocaleDateString("en-US", { weekday: "short" })}
                </span>
                <span
                  className="text-caption-c2"
                  style={{
                    marginTop: "-4px",
                    color: isSelected ? "#008cff" : "#d0d8df",
                  }}
                >
                  {day.getDate()}
                </span>
              </div>

              {(isPast || isCurrent) &&
                dayStatus?.mit_count != null &&
                (dayStatus.mit_count === 4 ? (
                  <img src="/icons/calender-circle-tick.svg" alt="Tick" />
                ) : (
                  <span
                    className="text-caption-c2"
                    style={{ color: "#959ea6", marginTop: "1px" }}
                  >
                    {dayStatus.mit_count}/4
                  </span>
                ))}
            </div>
          );
        })}
      </div>
      <div className="calender-btn" id="nextWeek" onClick={handleNextWeek}>
        <img
          src="/icons/calender-next-day.svg"
          alt="Next Week"
          style={{ cursor: "pointer" }}
        />
      </div>
    </div>
  );
});

CalendarComponent.displayName = "CalendarComponent";

export default CalendarComponent;
